import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { apiRequest } from "../api";
import { buildDocumentTimestamp } from "../utils/documentTimestamp";

import BusinessHeader from "../documents/layouts/BusinessHeader";
import ClientBlock from "../documents/layouts/ClientBlock";
import LineItemsTable from "../documents/layouts/LineItemsTable";
import TotalsBox from "../documents/layouts/TotalsBox";

import "../documents/styles/print.css";

function money(n) {
  if (n === null || n === undefined || n === "") return "$0.00";
  const v = typeof n === "string" ? Number(n) : n;
  return (isNaN(v) ? 0 : v).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return String(d || "—");
  }
}

function PrintPageFooter({ businessName, invoiceNumber, page, totalPages }) {
  return (
    <div className="jt-invoice-page-footer">
      <span>{businessName}</span>
      <span>Invoice {invoiceNumber} - Page {page} of {totalPages}</span>
    </div>
  );
}

function InvoiceHeading({ invoiceNumber, label = "Invoice Details" }) {
  return (
    <div className="jt-invoice-continuation-heading jt-document-section">
      <div className="jt-invoice-heading-label">{label}</div>
      <div className="jt-invoice-continuation-number">
        Invoice {invoiceNumber}
      </div>
    </div>
  );
}

function PaymentHistory({ payments }) {
  if (!payments.length) return null;

  return (
    <div className="jt-document-section jt-invoice-payment-history">
      <div className="jt-invoice-section-title">Payments Received</div>
      <table className="jt-invoice-payment-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Method</th>
            <th>Reference</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          {payments.map((payment) => (
            <tr key={payment.id}>
              <td>{fmtDate(payment.received_at)}</td>
              <td>{payment.method || "—"}</td>
              <td>{payment.reference || "—"}</td>
              <td>{money(payment.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const FIRST_PAGE_ITEM_COUNT = 7;
const CONTINUATION_ITEM_COUNT = 12;

function paginateItems(items) {
  if (items.length <= FIRST_PAGE_ITEM_COUNT) return [items];

  const pages = [items.slice(0, FIRST_PAGE_ITEM_COUNT)];
  for (let index = FIRST_PAGE_ITEM_COUNT; index < items.length; index += CONTINUATION_ITEM_COUNT) {
    pages.push(items.slice(index, index + CONTINUATION_ITEM_COUNT));
  }
  return pages;
}

function lineUnits(value) {
  return Math.max(1, Math.ceil(String(value || "").trim().length / 78));
}

function chunkByUnits(items, maxUnits) {
  const chunks = [];
  let chunk = [];
  let used = 0;

  items.forEach((item) => {
    const units = lineUnits(item);
    if (chunk.length > 0 && used + units > maxUnits) {
      chunks.push(chunk);
      chunk = [];
      used = 0;
    }
    chunk.push(item);
    used += units;
  });

  if (chunk.length > 0) chunks.push(chunk);
  return chunks;
}

function paginateDetailBlocks({
  exclusions,
  conditions,
  paymentSchedule,
  terms,
}) {
  const blocks = [];

  chunkByUnits(exclusions, 18).forEach((items, index) => {
    blocks.push({
      type: "exclusions",
      items,
      units: 4 + items.reduce((sum, item) => sum + lineUnits(item), 0),
      continued: index > 0,
    });
  });

  chunkByUnits(conditions, 18).forEach((items, index) => {
    blocks.push({
      type: "conditions",
      items,
      units: 4 + items.reduce((sum, item) => sum + lineUnits(item), 0),
      continued: index > 0,
    });
  });

  if (paymentSchedule.length > 0) {
    blocks.push({
      type: "schedule",
      items: paymentSchedule,
      units: 6 + paymentSchedule.length,
    });
  }

  if (terms) {
    blocks.push({
      type: "terms",
      text: terms,
      units: 4 + lineUnits(terms),
    });
  }

  const pages = [];
  let page = [];
  let used = 0;

  blocks.forEach((block) => {
    if (page.length > 0 && used + block.units > 40) {
      pages.push(page);
      page = [];
      used = 0;
    }
    page.push(block);
    used += block.units;
  });

  if (page.length > 0) pages.push(page);
  return pages;
}

export default function InvoicePrint() {
  const { invoiceId } = useParams();
  const [inv, setInv] = useState(null);
  const [client, setClient] = useState(null);
  const [profile, setProfile] = useState(null);
  const [payments, setPayments] = useState([]);
  const [err, setErr] = useState("");

  async function load() {
    setErr("");
    try {
      const invoice = await apiRequest(`/invoices/${invoiceId}`);
      setInv(invoice);

      if (invoice?.client_id) {
        try {
          setClient(await apiRequest(`/clients/${invoice.client_id}`));
        } catch {
          setClient(null);
        }
      }

      try {
        setProfile(await apiRequest("/profile"));
      } catch {
        setProfile(null);
      }

      try {
        const invoicePayments = await apiRequest(`/invoices/${invoiceId}/payments`);
        setPayments(Array.isArray(invoicePayments) ? invoicePayments : []);
      } catch {
        setPayments([]);
      }
    } catch (e) {
      setErr(e.message || "Failed to load invoice.");
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  if (err) {
    return (
      <div style={{ padding: 24, color: "#fff", background: "#061a2f" }}>
        <p style={{ color: "#ff8b8b" }}>{err}</p>
        <button onClick={load}>Retry</button>
      </div>
    );
  }

  if (!inv) {
    return (
      <div style={{ padding: 24, color: "#fff", background: "#061a2f" }}>
        Loading…
      </div>
    );
  }

  const companyName =
    profile?.display_name?.trim() ||
    profile?.legal_name?.trim() ||
    "Your Company";

  const companyAddress = [
    profile?.address_line1,
    profile?.address_line2 ? `Suite ${profile.address_line2}` : null,
    [profile?.city, profile?.state, profile?.postal_code].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join("\n");

  const clientName =
    client?.company_name || client?.contact_name || client?.name || "Client";

  const clientAddress = [
    client?.address,
    client?.address2,
    [client?.city, client?.state, client?.zip || client?.postal_code]
      .filter(Boolean)
      .join(", "),
  ]
    .filter(Boolean)
    .join("\n");

  const items = inv.items || [];
  const itemPages = paginateItems(items);
  const balance = Number(inv.total || 0) - Number(inv.amount_paid || 0);
  const invoiceNumber = inv.invoice_number || `#${inv.id}`;
  const invoiceNoForTitle = inv.invoice_number || `INV-${inv.id}`;
  const jobPartForTitle = inv?.job_id ? `-JOB${inv.job_id}` : "";
  const exclusions = Array.isArray(inv.extra_exclusions) ? inv.extra_exclusions.filter(Boolean) : [];
  const conditions = Array.isArray(inv.extra_conditions) ? inv.extra_conditions.filter(Boolean) : [];
  const paymentSchedule = Array.isArray(inv.payment_schedule) ? inv.payment_schedule : [];
  const detailPages = paginateDetailBlocks({
    exclusions,
    conditions,
    paymentSchedule,
    terms: inv.document_terms || "",
  });
  const summaryOnOwnPage =
    itemPages.length > 1 ||
    (itemPages[0] || []).length > 4 ||
    payments.length > 4;
  const totalPages = itemPages.length + (summaryOnOwnPage ? 1 : 0) + detailPages.length;

  document.title = `Invoice-${invoiceNoForTitle}${jobPartForTitle}-${buildDocumentTimestamp()}`;

  return (
    <div className="jt-print-page">
      <div className="no-print jt-invoice-print-actions">
        <Link to="/invoices" style={{ color: "#00c2ff", fontWeight: 800, textDecoration: "none" }}>
          ← Back to Invoices
        </Link>

        <button
          onClick={() => window.print()}
          style={{
            padding: "10px 16px",
            borderRadius: 10,
            border: 0,
            background: "#00c2ff",
            color: "#062033",
            fontWeight: 900,
            cursor: "pointer",
          }}
        >
          Print / Save PDF
        </button>
      </div>

      <section className="jt-print-sheet jt-invoice-print-sheet">
        <BusinessHeader
          businessName={companyName}
          businessAddress={companyAddress}
          phone={profile?.phone}
          email={profile?.email}
          website={profile?.website}
          documentTitle="Invoice"
          documentNumber={invoiceNumber}
          issueDate={fmtDate(inv.issue_date)}
          dueDate={fmtDate(inv.due_date)}
          status={inv.status || "—"}
        />

        <ClientBlock
          title="Bill To"
          clientName={clientName}
          address={clientAddress}
          phone={client?.phone}
          email={client?.email}
        />

        <LineItemsTable items={itemPages[0]} money={money} />

        {itemPages.length === 1 && !summaryOnOwnPage && (
          <InvoiceSummary
            inv={inv}
            payments={payments}
            balance={balance}
          />
        )}

        <PrintPageFooter
          businessName={companyName}
          invoiceNumber={invoiceNumber}
          page={1}
          totalPages={totalPages}
        />
      </section>

      {itemPages.slice(1).map((pageItems, index) => {
        const page = index + 2;
        const isFinalPage = page === totalPages;

        return (
          <section
            className="jt-print-sheet jt-invoice-print-sheet jt-invoice-continuation-page"
            key={page}
          >
            <InvoiceHeading invoiceNumber={invoiceNumber} />
            <LineItemsTable items={pageItems} money={money} />

            {isFinalPage && !summaryOnOwnPage && (
              <InvoiceSummary
                inv={inv}
                payments={payments}
                balance={balance}
              />
            )}

            <PrintPageFooter
              businessName={companyName}
              invoiceNumber={invoiceNumber}
              page={page}
              totalPages={totalPages}
            />
          </section>
        );
      })}

      {summaryOnOwnPage && (
        <section className="jt-print-sheet jt-invoice-print-sheet jt-invoice-continuation-page">
          <InvoiceHeading invoiceNumber={invoiceNumber} label="Invoice Summary" />
          <InvoiceSummary inv={inv} payments={payments} balance={balance} />
          <PrintPageFooter
            businessName={companyName}
            invoiceNumber={invoiceNumber}
            page={itemPages.length + 1}
            totalPages={totalPages}
          />
        </section>
      )}

      {detailPages.map((blocks, index) => {
        const pageNumber = itemPages.length + (summaryOnOwnPage ? 1 : 0) + index + 1;
        return (
          <section
            className="jt-print-sheet jt-invoice-print-sheet jt-invoice-continuation-page"
            key={`details-${pageNumber}`}
          >
            <InvoiceHeading invoiceNumber={invoiceNumber} />
            <div className="jt-estimate-full-width-stack">
              {blocks.map((block, blockIndex) => {
                if (block.type === "exclusions" || block.type === "conditions") {
                  const title = block.type === "exclusions" ? "Exclusions" : "Conditions";
                  return (
                    <div className="jt-document-section" style={compactDocumentBoxStyle} key={`${block.type}-${blockIndex}`}>
                      <div style={documentHeadingStyle}>
                        {title}{block.continued ? " (continued)" : ""}
                      </div>
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {block.items.map((item, itemIndex) => (
                          <li key={itemIndex} style={{ marginBottom: 4 }}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  );
                }

                if (block.type === "schedule") {
                  return (
                    <div className="jt-document-section" style={compactDocumentBoxStyle} key={`schedule-${blockIndex}`}>
                      <div style={documentHeadingStyle}>Payment Schedule</div>
                      <table className="jt-document-table">
                        <thead>
                          <tr>
                            <th style={scheduleHeaderStyle}>Payment</th>
                            <th style={{ ...scheduleHeaderStyle, textAlign: "right" }}>Percent</th>
                            <th style={{ ...scheduleHeaderStyle, textAlign: "right" }}>Amount</th>
                            <th style={scheduleHeaderStyle}>Due</th>
                          </tr>
                        </thead>
                        <tbody>
                          {block.items.map((item, itemIndex) => {
                            const percent = Number(item?.percent || 0);
                            const amount = (Number(inv.total || 0) * percent) / 100;
                            return (
                              <tr key={itemIndex}>
                                <td style={scheduleCellStyle}>{item?.label || `Payment ${itemIndex + 1}`}</td>
                                <td style={{ ...scheduleCellStyle, textAlign: "right" }}>{percent}%</td>
                                <td style={{ ...scheduleCellStyle, textAlign: "right" }}>{money(amount)}</td>
                                <td style={scheduleCellStyle}>{item?.due_when || "—"}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                }

                return (
                  <div className="jt-document-section" style={compactDocumentBoxStyle} key={`terms-${blockIndex}`}>
                    <div style={documentHeadingStyle}>Terms</div>
                    <div style={{ whiteSpace: "pre-wrap" }}>{block.text}</div>
                  </div>
                );
              })}
            </div>
            <PrintPageFooter
              businessName={companyName}
              invoiceNumber={invoiceNumber}
              page={pageNumber}
              totalPages={totalPages}
            />
          </section>
        );
      })}
    </div>
  );
}

function InvoiceSummary({ inv, payments, balance }) {
  return (
    <div className="jt-invoice-final-summary">
      <PaymentHistory payments={payments} />
      <div className="jt-invoice-summary-grid jt-document-section">
        <div className="jt-invoice-payment-message">
          <div className="jt-invoice-section-title">Payment Information</div>
          <div className="jt-invoice-payment-copy">
            {inv.document_payment_note || "Please include the invoice number with your payment."}
          </div>
          <div className="jt-invoice-thank-you">Thank you for your business.</div>
        </div>
        <TotalsBox
          subtotal={inv.subtotal}
          tax={inv.tax_total}
          total={inv.total}
          paid={inv.amount_paid}
          balance={balance}
          money={money}
        />
      </div>
    </div>
  );
}

const documentHeadingStyle = {
  fontSize: 12,
  fontWeight: 900,
  color: "#8a6a00",
  letterSpacing: 0.7,
  textTransform: "uppercase",
  marginBottom: 8,
};

const compactDocumentBoxStyle = {
  border: "1px solid #dbe7f3",
  borderRadius: 14,
  padding: 11,
  background: "#fbfdff",
  color: "#405366",
  fontSize: 12,
  lineHeight: 1.32,
};

const scheduleHeaderStyle = {
  borderBottom: "1px solid #c9d7e5",
  padding: "7px 8px",
  textAlign: "left",
  color: "#405366",
  fontSize: 11,
  textTransform: "uppercase",
};

const scheduleCellStyle = {
  borderBottom: "1px solid #e6edf4",
  padding: "6px 8px",
  verticalAlign: "top",
};
